import { isActionRecordWithActionArgs } from "@buildwithflux/models";
import { lzCompressField, lzDecompressField } from "@buildwithflux/shared";
import { cloneDeep } from "lodash";
import { flatObjectToInterface, interfaceDataToFlatObject } from "../common";
/**
 * This converter is a little different because for action records, we actually use the metadata
 * within the storage layer to perform business logic i.e. to ignore action records that were generated
 * by the same client that wrote them.  So the typing looks a little different from other converters.
 * See the FirestoreActionRecordRepository for more details.
 */
/**
 * Schema versions
 */
export var ActionRecordSchemaVersion;
(function (ActionRecordSchemaVersion) {
    /**
     * Legacy storage format - no compression at all.
     */
    ActionRecordSchemaVersion[ActionRecordSchemaVersion["uncompressed"] = 0] = "uncompressed";
    /**
     * In version 1 we compress the arguments and diff of action records.
     */
    ActionRecordSchemaVersion[ActionRecordSchemaVersion["compressedDiffAndActionFields"] = 1] = "compressedDiffAndActionFields";
})(ActionRecordSchemaVersion || (ActionRecordSchemaVersion = {}));
/**
 * The schema version to write all new data with
 *
 * This is currently uncompressed so that we can upgrade all readers to support reading compressed data before writing
 * any.
 */
export const currentActionRecordSchemaVersionToWrite = ActionRecordSchemaVersion.compressedDiffAndActionFields;
function getActionRecordSchemaVersion(stored) {
    return stored.metadata?.actionRecordSchemaVersion || ActionRecordSchemaVersion.uncompressed;
}
/**
 * Load action record data from an object by delegating to functions that can read data with a given schema.
 *
 * @param rawStoredData Data of unknown type or schema version.
 * @returns Action record data, as well as the metadata corresponding to the write.
 * @throws Error if the schema version is not recognized.
 */
export function deserialize(rawStoredData) {
    const storedData = rawStoredData;
    const raw = flatObjectToInterface(rawStoredData);
    if (getActionRecordSchemaVersion(storedData) >= ActionRecordSchemaVersion.compressedDiffAndActionFields) {
        const compressedRecord = raw.data;
        lzDecompressField(compressedRecord, "actionChangeDiff");
        // HACK: Copy the code version into IActionRecord for use in the application
        raw.data.actionCodeVersion = storedData.metadata.codeVersion;
    }
    return raw;
}
/**
 * Convert an IActionRecord that _already has metadata_ into an "enriched" version that carries
 * compressed fields as well as additional metadata that we use during the deserialization process.
 */
export function serialize(data, actionRecordSchemaVersionToWrite = currentActionRecordSchemaVersionToWrite) {
    const result = cloneDeep(data);
    if (result.data && actionRecordSchemaVersionToWrite >= ActionRecordSchemaVersion.compressedDiffAndActionFields) {
        const uncompressedRecord = result.data;
        lzCompressField(uncompressedRecord, "actionChangeDiff");
        // NOTE: actionArgs was removed from the codebase with the switch to
        // seamless baking because there was no longer any need to rerun
        // actions, but we keep compressing existing legacy data
        if (isActionRecordWithActionArgs(uncompressedRecord)) {
            lzCompressField(uncompressedRecord, "actionArgs");
        }
    }
    return interfaceDataToFlatObject({
        data: result.data,
        metadata: {
            ...result.metadata,
            actionRecordSchemaVersion: actionRecordSchemaVersionToWrite,
        },
    });
}
export class ActionRecordAdminConverter {
    actionRecordSchemaVersionToWrite;
    constructor(actionRecordSchemaVersionToWrite = currentActionRecordSchemaVersionToWrite) {
        this.actionRecordSchemaVersionToWrite = actionRecordSchemaVersionToWrite;
    }
    toFirestore(modelObject) {
        return serialize(modelObject, this.actionRecordSchemaVersionToWrite);
    }
    fromFirestore(snapshot) {
        return deserialize(snapshot.data());
    }
}
export class ActionRecordClientConverter {
    actionRecordSchemaVersionToWrite;
    constructor(actionRecordSchemaVersionToWrite = currentActionRecordSchemaVersionToWrite) {
        this.actionRecordSchemaVersionToWrite = actionRecordSchemaVersionToWrite;
    }
    toFirestore(modelObject) {
        return serialize(modelObject, this.actionRecordSchemaVersionToWrite);
    }
    fromFirestore(snapshot) {
        return deserialize(snapshot.data());
    }
}
//# sourceMappingURL=ActionRecordConverter.js.map